import { type UseQueryResult, useQuery } from "react-query";

import { getInternalRelatedRecords, getRelatedRecords } from "../../../../../api";
import { useFormContext } from "../../../../../hooks/useFormContext";
import { useIsPreview } from "../../../../../hooks/useIsPreview";
import { Fields } from "../../../../../types/fields";
import type { BaseRecord, FormConfigApplicationStructureItem } from "../../../../../types/form";

interface UseLinkedRecordRelatedRecordsParams {
  field: FormConfigApplicationStructureItem;
  visibleFields: string[];
}

export const useLinkedRecordRelatedRecords = ({
  field,
  visibleFields,
}: UseLinkedRecordRelatedRecordsParams): UseQueryResult<BaseRecord[] | undefined> => {
  const formContext = useFormContext();
  const { isPreview } = useIsPreview();

  return useQuery<BaseRecord[] | undefined>(
    [
      `${Fields.linkedrecordfield}_${field.slug}_${field.params?.linked_application}_records_with_related`,
      // visibleFields decides what the endpoint resolves, and it is still empty on the first
      // render, before the linked application's fields arrive. Keying on it turns that stale
      // first result into a refetch instead of a value cached for the life of the form.
      visibleFields.join(","),
    ],
    async () => {
      if (
        !formContext.accountId ||
        (!formContext.isInternal && !formContext.config.sharing_hash) ||
        !field.params?.linked_application
      ) {
        return undefined;
      }

      if (formContext.isInternal && formContext.reportId) {
        return await getInternalRelatedRecords({
          accountId: formContext.accountId,
          reportId: formContext.reportId,
          linkedAppId: field.params.linked_application,
          visibleFields,
        });
      }

      return await getRelatedRecords({
        accountId: formContext.accountId,
        linkedAppId: field.params.linked_application,
        hash: formContext.config.sharing_hash,
        isPreview,
        visibleFields,
        passcode: formContext.passcode,
      });
    },
    {
      staleTime: Infinity,
    },
  );
};
